import React, { Component } from "react";
import { setAndGetAttributes } from "../../FormInputFields/FormInputFunctions";
import Wrp from "../../../hoc/Wrp/Wrp";
import Autocomplete from "../../AutoComplete";
import { callGET } from "../../../Utils/ApiUtil";
import { IsJsonString, isSet } from "../../../Utils/CommonFunctions";
import Icons from "../../Icons/Icons";
import PagesServices from "../../../services/PagesServices";
import http from "../../../services/http";
import { extractLookupIcon, extractConfigAttributes, renderNodeChildren } from "../../../Containers/PagesContainer/PagesContainerUtils";
import LookupModal from "./LookupModal";

class PagesLookup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      isLoading: false,
      displayName: "",
      lookupAutoComplete: [],
      name: "",
      typing: false,
      typingTimeout: 0,
      config: {
        apiAction: "",
        searchField: "",
        offset: "",
        limit: "",
        resultVar: "",
        recordIdField: "",
        defaultField: "",
        resultFields: [], // Array of {key, label} objects
      },
      pageConfig: null,
      inputVariables: [],
      outputVariables: [],
      pageConfigId: null,
    };
  }

  componentDidMount = () => {
    // Initialize with current value if available
    const {childrenProps,value} = this.props;
    // Extract config attributes from childrenProps
    if (childrenProps) {
      const config = extractConfigAttributes(childrenProps);
      if (config) {
        this.setState({ config }, () => {
          // Fetch page config and variables once if apiAction is available
          if (isSet(config.apiAction,null) !== null) {
            this.fetchPageConfigAndVariables(config.apiAction);
          }
          // After fetching initial data, update displayName if value exists
          if (value) {
            this.updateDisplayNameFromValue(value);
          }
        });
      } else if (value) {
        // If no config, still try to update display name
        this.updateDisplayNameFromValue(value);
      }
    } else if (value) {
      this.updateDisplayNameFromValue(value);
    }
  }

  componentDidUpdate = (prevProps) => {
    // Update display name when value prop changes
    const {childrenProps,value} = this.props;
    if (prevProps.value !== value) {
      // Find the label for this value from autocomplete suggestions
      if (value) {
        this.updateDisplayNameFromValue(value);
      } 
      // else {
      //   this.setState({ displayName: "" });
      // }
    }
    // Update config if childrenProps changed
    if (prevProps.childrenProps !== childrenProps && childrenProps) {
      const config = extractConfigAttributes(childrenProps);
      if (config) {
        const prevApiAction = this.state.config.apiAction;
        this.setState({ config }, () => {
          // Fetch page config and variables once if apiAction changed and is available
          if (isSet(config.apiAction,null) !== null && config.apiAction !== prevApiAction) {
            this.fetchPageConfigAndVariables(config.apiAction);
          }
        });
      }
    }
  }

  // Helper function to update displayName from value by finding it in autocomplete suggestions
  updateDisplayNameFromValue = (valueId) => {
    const { lookupAutoComplete } = this.state;
    // Find the label for this value ID
    const foundItem = lookupAutoComplete.find(item => item.value === valueId || item.value === String(valueId));
    if (foundItem) {
      this.setState({ displayName: foundItem.label });
    } else {
      // If not found in current suggestions, keep the value as fallback or empty
      // The label will be updated when autocomplete data is loaded
      // this.setState({ displayName: "" });
    }
  }

  // Helper function to get nested property value using dot notation (e.g., "country.name")
  getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
      if (value === null || value === undefined) {
        return undefined;
      }
      value = value[key];
    }
    return value;
  };

  // Fetch page config and input/output variables once (not on every search)
  fetchPageConfigAndVariables = async (apiAction) => {
    try {
      // Get page config by API name
      const pageConfigResponse = await PagesServices.getConfigByName(apiAction);
      
      if (pageConfigResponse && pageConfigResponse.code === 200 && pageConfigResponse.data) {
        const pageConfig = pageConfigResponse.data;
        const pageConfigId = pageConfig.id;
        // Get input and output variables from page config
        const variablesResponse = await PagesServices.getConfigVariablesList(pageConfigId);
        
        if (variablesResponse && variablesResponse.code === 200 && variablesResponse.data) {
          const inputVariables = variablesResponse.data.content.filter(v => v.is_request === true);
          const outputVariables = variablesResponse.data.content.filter(v => v.is_response === true);
          
          // Store in state for reuse
          this.setState({
            pageConfig,
            pageConfigId,
            inputVariables,
            outputVariables,
          }, () => {
            // After config and variables are loaded, fetch initial lookup suggestions
            this.fetchLookupSuggestions('');
          });
        } else {
          console.error('Failed to get variables for page config:', pageConfigId);
        }
      } else {
        console.error('Failed to get page config by API name:', apiAction);
      }
    } catch (error) {
      console.error('Error fetching page config and variables:', error);
    }
  }


  // Fetch lookup suggestions
  fetchLookupSuggestions = async (searchValue) => {
    const { windowName, childrenProps } = this.props;
    let { config } = this.state;
    const {searchField,offset,limit} = config || {};

    // If config is not in state yet, try to extract it from childrenProps
    if (!config || (!config.apiAction && childrenProps)) {
      const extractedConfig = extractConfigAttributes(childrenProps);
      if (extractedConfig) {
        config = extractedConfig;
        this.setState({ config });
      }
    }
    const hasApiAction = config && config.apiAction && config.apiAction.trim() !== "";
    this.setState({ isLoading: true });
    
    // Use config values if available, otherwise use defaults
    const page = 0;
    const perPage = 10;
    const search = searchValue || "";
    const offsetField = offset;
    const limitField = limit;
    // Build query string with config field names
    const queryParams = new URLSearchParams();
    queryParams.append(searchField, search);
    queryParams.append(offsetField, page.toString());
    queryParams.append(limitField, perPage.toString());

    const queryString = `?${queryParams.toString()}`;

    try {
      let API_URL;
      let response;
      if (hasApiAction) {
        // Use cached page config and input variables (fetched once)
        let { pageConfigId, inputVariables, pageConfig } = this.state;
        
        if (!pageConfigId || !inputVariables || inputVariables.length === 0 || !pageConfig) {
          // If not cached yet, fetch them now (shouldn't happen, but as fallback)
          console.warn('Page config or variables not cached, fetching now...');
          await this.fetchPageConfigAndVariables(config.apiAction);
          // Get updated values from state after fetch
          pageConfigId = this.state.pageConfigId;
          inputVariables = this.state.inputVariables;
          pageConfig = this.state.pageConfig;
          
          if (!pageConfigId || !inputVariables || inputVariables.length === 0 || !pageConfig) {
            console.error('Failed to get page config or variables');
            this.setState({ isLoading: false, lookupAutoComplete: [] });
            return;
          }
        }
        
        // Get from_page_mapping from config (similar to PagesRenderComponent pattern)
        const fromMapping = pageConfig.from_page_mapping || [];
        
        // Create inputMap similar to PagesRenderComponent
        const inputMap = Object.fromEntries(
          inputVariables.map(item => [item.id, item.api_name])
        );

        // Build payload using from_page_mapping (similar to PagesRenderComponent)
        const payload = fromMapping.map(item => {
          const apiName = isSet(item?.pages_variable_name, inputMap[item.pages_variable_id]);
          
          let selectedValue = '';
          // Handle special fields from config
          if (apiName === searchField) {
            selectedValue = search;
          } else if (apiName === offsetField) {
            selectedValue = page.toString();
          } else if (apiName === limitField) {
            selectedValue = perPage.toString();
          }
          
          return {
            ...item,
            selected_value: isSet(selectedValue, ''),
          };
        });
        
        // console.log('Pages API Payload:', payload);
        // Use POST method with payload - only execute API is called here
        API_URL = `pageconfig/${pageConfigId}/pages-config-execute`;
        response = await http.POST(API_URL, payload)
      }
      this.setState({ isLoading: false });
      if (response.status === 200) {
        const responseData = response.data;
        if (responseData.code === 200) {
          let updateTableData = [];
          
            // Pages API response structure - extract variables from response.data.variables
            const responseVariables = responseData.data?.variables || [];
            const { outputVariables, pageConfig } = this.state;
            const { resultVar,defaultField, recordIdField, resultFields } = config || {};
            // console.log('Response variables:', responseVariables);
            // console.log('Page config:', pageConfig);
            // console.log('Config - resultVar:', resultVar, 'recordIdField:', recordIdField, 'resultFields:', resultFields);
            // console.log('Output variables:', outputVariables);
            if (responseVariables && responseVariables.length > 0 && resultVar && pageConfig) {
              const toPageMapping = pageConfig.to_page_mapping || [];
              const variableMapping = {};
              
              toPageMapping.forEach((toMap) => {
                if (toMap.field_mappings && Array.isArray(toMap.field_mappings)) {
                  toMap.field_mappings.forEach((mField) => {
                    if (mField.automation_variable_id && mField.pages_variable_name) {
                      variableMapping[mField.automation_variable_id] = mField.pages_variable_name;
                    }
                  });
                }
              });
              

              const responseVariableMap = {};
              responseVariables.forEach((responseVar) => {
                // Find the page variable name for this response variable using automation_variable_id
                const pageVariableName = variableMapping[responseVar.id];
                if (pageVariableName) {
                  responseVariableMap[pageVariableName] = responseVar;
                }
              });
              
            //   console.log('Response variable map (mapped to page variable names):', responseVariableMap);
            //   console.log('Looking for resultVar:', resultVar);
              
              // Find the result variable using pages_variable_name (e.g., "lookupObj")
              const resultVariable = responseVariableMap[resultVar];
              console.log('resultVariable (mapped by page variable name):', resultVariable);
              
              const resultOutputVariable = outputVariables.find(ov => {
                // Try to match by api_name first (if pages_variable_name is same as api_name)
                if (ov.api_name === resultVar) {
                  return true;
                }
                // Otherwise, we might need to check if there's a mapping
                // For now, try api_name match
                return false;
              });
              
              console.log('Result output variable definition:', resultOutputVariable);
              
              if (resultVariable) {
                let resRecordsData = [];
                // Try different possible locations for the value
                try{
                    const recordsData = isSet(resultVariable.value,resultVariable.selected_value);
                    resRecordsData = IsJsonString(recordsData)?JSON.parse(recordsData):recordsData;
                    if (!Array.isArray(resRecordsData)) {
                      resRecordsData = resRecordsData ? [resRecordsData] : [];
                    }
                    resRecordsData.forEach((record) => {
                        console.log('record',record,defaultField);
                        let displayLabel = '';
                        // If defaultField exists, use it (supports nested paths like "country.name")
                        if (defaultField) {
                          displayLabel = defaultField.includes('.')
                            ? this.getNestedValue(record, defaultField)
                            : record[defaultField];
                        } else if (resultFields && resultFields.length > 0) {
                          // Build label from first resultField (supports nested paths)
                          const firstField = resultFields[0];
                          displayLabel = firstField.key.includes('.')
                            ? this.getNestedValue(record, firstField.key)
                            : record[firstField.key];
                        } else if (recordIdField) {
                          // Use recordIdField (supports nested paths)
                          displayLabel = recordIdField.includes('.')
                            ? this.getNestedValue(record, recordIdField)
                            : record[recordIdField];
                        } else {
                          // Use first available field value
                          const firstKey = Object.keys(record)[0];
                          displayLabel = record[firstKey] || '';
                        }
                        
                        // Get record ID (supports nested paths)
                        const recordId = recordIdField && recordIdField.includes('.')
                          ? this.getNestedValue(record, recordIdField)
                          : record[recordIdField] || record.id || '';
                        
                        if (displayLabel) {
                          updateTableData.push({
                            label: String(displayLabel),
                            value: recordId,
                          });
                        }
                      });
                }catch(error){
                    console.error('Error parsing records data:', error);
                }
                
               
                this.setState({ lookupAutoComplete: updateTableData }, () => {
                  // After updating autocomplete data, update displayName if value exists
                  const { value } = this.props;
                  if (value) {
                    this.updateDisplayNameFromValue(value);
                  }
                });
               
              } else {
                console.warn('Result variable not found in mapped variables:', resultVar, 'Available mapped variables:', Object.keys(responseVariableMap));
              }
            } else {
              console.warn('No variables found in response or resultVar not configured:', {
                responseVariables,
                resultVar
              });
            }
         
        } else {
        //   this.setState({ lookupAutoComplete: [] });
        }
      }
    } catch (err) {
      console.error('Lookup API error:', err);
      this.setState({ isLoading: false, lookupAutoComplete: [] });
    }
  };

  autoCompleteSelect = (obj) => {
    const { changeHandler } = this.props;
    this.setState({ displayName: obj.label });
    console.log('autoCompleteSelect called with obj:', obj);
    if (changeHandler) {
      changeHandler(obj);
    }
  };

  autoCompleteSearch = (value) => {
    console.log('autoCompleteSearch called with value:', value);
    this.setState({ displayName: value });
    const self = this;

    if (self.state.typingTimeout) {
      clearTimeout(self.state.typingTimeout);
    }

    self.setState({
      name: value,
      typing: false,
      typingTimeout: setTimeout(function () {
        console.log('Calling fetchLookupSuggestions with value:', value);
        self.fetchLookupSuggestions(value);
      }, 300),
    });
  };

  openLoopupModalHandler = () => {
    this.setState({ isModalOpen: true });
  };

  cancelLoopupModalHandler = () => {
    this.setState({ isModalOpen: false });
  };

  setLookupValuesFromList = (lookupData) => {
    const { changeHandler } = this.props;
    this.setState({ 
      displayName: lookupData.label,
      isModalOpen: false 
    });
    if (changeHandler) {
        console.log('lookupData',lookupData);
      changeHandler(lookupData);
    }
  };


//   fieldOnChangeHandler = (e) => {
//     const { changeHandler, isEvent } = this.props;
//     if (isEvent === true) {
//       changeHandler(e.target.value, e);
//     } else {
//       changeHandler(e.target.value);
//     }
//   };

  render() {
    const { value, childrenProps, required, className, attributes } = this.props;
    const { displayName, lookupAutoComplete, isLoading, config } = this.state;
    // console.log('lookupAutoComplete',config,lookupAutoComplete);
    // Extract windowName from props or attributes
    const windowName = this.props.windowName || (attributes && attributes.windowName) || "";

    let attr = setAndGetAttributes(this.props);

    // Extract custom icon from children props
    let customLookupIcon = null;
    if (childrenProps) {
      customLookupIcon = extractLookupIcon(childrenProps);
    }

    // Config attributes are available in this.state.config:
    // - config.apiAction: API name from Config action attribute (e.g., "lookup_test")
    // - config.searchField: Search field name from <Search> tag (e.g., "search_input")
    // - config.offset: Offset field name from <Pagination><Offset> tag (e.g., "page_input")
    // - config.limit: Limit field name from <Pagination><Limit> tag (e.g., "limit_input")
    // - config.resultVar: Result variable name from <Result var> attribute (e.g., "lookupObj")
    // - config.recordIdField: Record ID field name from <Result recordId> attribute (e.g., "id")
    // - config.resultFields: Array of field objects from <Result><Field> tags (e.g., [{key: "CompanyName", label: "Company Name"}])

    // Default icon if no custom icon
    const defaultIcon = windowName ? (
      <Icons iconType={windowName} size={16} color={"#000"} />
    ) : (
      <i className="fas fa-info"></i>
    );

    const { isModalOpen } = this.state;
    // console.log('displayName',displayName);
    return (
      <Wrp>
        <div className={`input-group autocomplete`}>
          <input
            type="hidden"
            value={value || ""}
            {...attr}
            autoComplete="new-lookup"
          />
          <Autocomplete
            attributes={attr}
            suggestions={lookupAutoComplete}
            search={this.autoCompleteSearch}
            select={this.autoCompleteSelect}
            selectedValue={displayName}
            isDisable={attr.readOnly || false}
            isLoading={isLoading}
            windowName={windowName}
          />
          {!(attr.readOnly || false) ? (
            <div
              className={required === true ? "input-group-append mandatory" : "input-group-append"}
              onClick={this.openLoopupModalHandler}
            >
              <a className="input-group-text">
                {customLookupIcon ? (
                  <span dangerouslySetInnerHTML={{ __html: renderNodeChildren(customLookupIcon) }} />
                ) : (
                  defaultIcon
                )}
              </a>
            </div>
          ) : null}
        </div>
        <LookupModal
          isModalOpen={isModalOpen}
          windowName={windowName}
          selectedValue={value}
          childrenProps={childrenProps}
          cancelLoopupModalHandler={this.cancelLoopupModalHandler}
          setLookupValuesFromList={this.setLookupValuesFromList}
        />
      </Wrp>
    );
  }
}

PagesLookup.defaultProps = {
  isMandatory: false,
  isEvent: false,
  className: "form-control custom-arrow",
  required: false,
};

export default PagesLookup;
